import simbApi from ".";
import { DETAILED_USAGE_DATA, USAGE_DATA, USAGE_REFRESH } from "../../data/constants/endpoints";
import logger from "../../lib/logger";

export const fetchUsage = async (access_token: string) => {
  try {
    const response = await simbApi.get(USAGE_DATA, {
      headers: { Authorization: `Bearer ${access_token}` },
    });

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data.usageData;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

export interface UsageRefreshResponse {
  /** Same shape as `usageData`; null for an account that has never been calculated. */
  usage: Record<string, unknown> | null;
  lastCalculatedAt: string | null;
  refresh_requested: boolean;
}

/**
 * "Give me the current usage and make sure a refresh is running if it is
 * stale." Returns whatever the backend has cached; a recalculation, if one was
 * needed, runs server-side and its result shows up on a later call.
 */
export const requestUsageRefresh = async (access_token: string): Promise<UsageRefreshResponse> => {
  try {
    const response = await simbApi.post(
      USAGE_REFRESH,
      {},
      { headers: { Authorization: `Bearer ${access_token}` } },
    );

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data;
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};

/**
 * What the usage UI fetches on open. The cached value paints immediately; only
 * an account with no cache yet (first ever open, calculation still running)
 * falls back to the synchronous computation so the first paint is not empty.
 */
export const fetchUsageWithRefresh = async (access_token: string) => {
  const refresh = await requestUsageRefresh(access_token);
  if (refresh?.usage) return refresh.usage;
  return fetchUsage(access_token);
};

export const fetchDetailedUsage = async (access_token: string) => {
  try {
    const response = await simbApi.get(DETAILED_USAGE_DATA, {
      headers: { Authorization: `Bearer ${access_token}` },
    });

    if (response?.data?.error) {
      throw new Error(response?.data?.error);
    }

    return response?.data?.activeBoards ?? [];
  } catch (error: any) {
    logger.error(error.message);
    throw error;
  }
};
